import fp from 'lodash/fp';
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';

import { useSelector, useAction } from 'Modules/core/redux';
import { useTranslation } from 'Modules/core/i18n';
// TODO: remove cycle dependency for bapp-fapp modules
import { bappActions } from 'Modules/business/bapp/state/actions';
import { useStorageItem } from 'Modules/business/data';
import { downloadsActions, useDownloads } from 'Modules/business/downloads';

import { DownloadPopup, SectionHeader, FileSection, FileItem } from '../../DownloadPopup';


const documentModel = ({
    documents,
    download,
    documentsDownloads,
}) => fp.map(
    ({ DocumentTypeName, DocumentTypeCode, Number, Date, Uid }) => ({
        title: DocumentTypeName || DocumentTypeCode,
        description: Number,
        date: Date,
        onDownload: () => download('documents', { id: Uid }),
        progress: documentsDownloads[Uid],
    }),
    documents,
);

const DocumentSection = ({ files }) => (
    <FileSection>
        {files.map((file, idx) => (
            <FileItem key={idx} {...file} />
        ))}
    </FileSection>
);

export const DownloadButton = ({ documentId }) => {
    const { t } = useTranslation('downloadButton');

    const document = useStorageItem('documents', documentId);

    const additionalDocuments = document.$additionalDocuments;
    const shouldDownloadAdditionalDocuments = additionalDocuments === undefined;
    const [documentDownloading, setDocumentDownloading] = useState(false);
    const documentDownloadingTimer = useRef();

    const downloadError = useAction(bappActions.downloadAdditionalDocuments);
    const onOpen = useCallback(
        () => {
            if (shouldDownloadAdditionalDocuments && !documentDownloading) {
                documentDownloadingTimer.current =
                    setTimeout(() => setDocumentDownloading(true), 100);
                downloadError({ uid: document.Uid });
            }
        },
        [document.Uid, documentDownloading, downloadError, shouldDownloadAdditionalDocuments],
    );

    useEffect(
        () => {
            if (!shouldDownloadAdditionalDocuments) {
                clearTimeout(documentDownloadingTimer.current);
                setDocumentDownloading(false);
            }

            return () => clearTimeout(documentDownloadingTimer.current);
        },
        [shouldDownloadAdditionalDocuments],
    );

    const documentsDownloads = useDownloads('documents');
    const download = useAction(downloadsActions.download);
    const files = useMemo(
        () => documentModel({
            documents: [
                document,
                ...additionalDocuments || [],
            ],
            download,
            documentsDownloads,
        }),
        [document, additionalDocuments, documentsDownloads, download],
    );

    const downloadInProgress = useSelector(
        ({ downloads }) => downloads.documents[documentId] ||
            additionalDocuments &&
            fp.some(({ Uid }) => downloads.documents[Uid], additionalDocuments),
        [documentId, additionalDocuments],
    );

    return (
        <DownloadPopup
            onOpen={onOpen}
            suppressPopup={shouldDownloadAdditionalDocuments}
            isDownloading={documentDownloading || downloadInProgress}
        >
            {!shouldDownloadAdditionalDocuments && (
                <>
                    <SectionHeader>
                        {t('documentSectionName')}
                    </SectionHeader>
                    <DocumentSection
                        t={t}
                        files={files}
                    />
                </>
            )}
        </DownloadPopup>
    );
};
